import { computed } from 'vue';
import { useUpc } from '@nuxt-common/media-card-large/app/composables/useUpc';
import useMembersOfProduct from '~/composables/useMembersOfProduct';
import { useProductType } from '~/composables/useProductType';
import { useProduct } from '~/modules/product/composables/useProduct';
import useSize from '~/modules/size/composables/useSize';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useSlideout } from '~/composables/utils/useSlideout';

/**
 * Size chart metadata for the current product context (top-level vs consolidated member).
 * we are passing in firstUpcKeyForColorAndDrivenTrait since we dont want to call that composable again
 * and potientially create multiple watchers from it and cause new bugs
 */
export default function useSizeChart(firstUpcKeyForColorAndDrivenTrait?: ComputedRef<string | number | null | undefined>) {
  const { isShopTheSetExpEnabled } = useFeatureEligibility();
  const { productData: product, productId: id } = useProduct();
  const { isConsolidatedMaster, isConsolidatedLatchKey } = useProductType();
  const { isConsolidatedThirdVariantFlag } = useProductFlags();
  const consolidatedProduct = isConsolidatedMaster || isConsolidatedLatchKey || isConsolidatedThirdVariantFlag || isShopTheSetExpEnabled;
  const { sizeProps } = useSize();
  const { memberProductsMap } = useMembersOfProduct();
  const { upcData, selectedUpcId } = useUpc(id);

  const selectedMemberId = useSelectedMemberId();
  const productId = computed(() => {
    if (consolidatedProduct) {
      return selectedMemberId.value;
    }
    return id;
  });

  if (useSlideout()?.isSlideoutAvailable.value) {
    return {
      sizeChartImage: computed(() => ''),
      isSizeChart: computed(() => false),
      productId,
      sizeChartId: computed(() => ''),
    };
  }

  // firstUpcKeyForColorAndDrivenTrait should always be defined when isConsolidatedThirdVariantFlag is true
  const upcIdSource = computed(
    () =>
      selectedUpcId.value || (isConsolidatedThirdVariantFlag && firstUpcKeyForColorAndDrivenTrait ? firstUpcKeyForColorAndDrivenTrait.value : null),
  );
  const selectedProductId = computed(() => upcData.value.upcs[upcIdSource.value || '']?.relationships?.selectedProduct);

  const consolidatedSizes = computed(() =>
    isShopTheSetExpEnabled
      ? memberProductsMap.value[selectedMemberId.value || '']?.traits?.sizes
      : memberProductsMap.value[selectedProductId.value || '']?.traits?.sizes,
  );
  const selectedConsolidatedSizeChartId = computed(() => {
    return consolidatedSizes.value?.sizeChartId;
  });
  const selectedConsolidatedSizeChartImage = computed(() => {
    return consolidatedSizes.value?.sizeChartImg;
  });
  const isSizeChartForConsolidated = computed(() => selectedConsolidatedSizeChartId.value || selectedConsolidatedSizeChartImage.value);

  const sizeChartIdTopLevel = product?.traits?.sizes?.sizeChartId || '';
  const sizeChartImageTopLevel = product?.traits?.sizes?.sizeChartImg || '';
  const isSizeChartTopLevel = computed(() => (!!sizeChartIdTopLevel || !!sizeChartImageTopLevel) && sizeProps.value.sizes.length);

  const sizeChartImage = computed(() => (consolidatedProduct ? selectedConsolidatedSizeChartImage.value : sizeChartImageTopLevel));
  const isSizeChart = computed(() => (consolidatedProduct ? isSizeChartForConsolidated.value : isSizeChartTopLevel.value));
  const sizeChartId = computed(() => (consolidatedProduct ? selectedConsolidatedSizeChartId.value : sizeChartIdTopLevel));

  return {
    sizeChartImage,
    isSizeChart,
    productId,
    sizeChartId,
  };
}
